import React, { createRef, ForwardedRef, forwardRef, useEffect, useState } from 'react'
import { useDrop, XYCoord } from 'react-dnd'
import { SelectValue } from 'antd/lib/select'
import isNil from 'lodash/isNil'
import { BaseSelectRef } from 'rc-select'
import { CustomTagProps } from 'rc-select/lib/BaseSelect'

import { MultiSelect } from 'Shared/atoms'

import dragDropService from './utils/dragDropService'
import { updateValue } from './utils/helpers'
import DraggableItem from './DraggableItem'
import {
	ITEM_TYPE,
	ItemRefType,
	MultiselectWithDraggableItemsProps,
	PotentialDropTarget
} from './types'

const MultiselectWithDraggableItems = forwardRef<
	BaseSelectRef,
	MultiselectWithDraggableItemsProps<SelectValue>
>(
	(
		{
			onUpdateValueArray,
			...props
		}: React.PropsWithChildren<MultiselectWithDraggableItemsProps<SelectValue>>,
		ref: ForwardedRef<BaseSelectRef>
	) => {
		const [itemsRefs, setItemsRefs] = useState<ItemRefType>({})

		const [potentialDropTarget, setPotentialDropTarget] = useState<PotentialDropTarget | null>(
			null
		)

		useEffect(() => {
			setItemsRefs(
				Array.from(props.value as LabelValue<number>[]).reduce(
					(acc, curr) => ({ ...acc, [curr.value.toString()]: createRef() }),
					{}
				)
			)
		}, [props.value])

		useEffect(() => {
			dragDropService.updateItemCoords(itemsRefs)
		}, [itemsRefs])

		const [, drop] = useDrop({
			accept: ITEM_TYPE,
			drop(item: LabelValue<number>) {
				if (isNil(potentialDropTarget)) {
					return
				}

				const updatedValue = updateValue({
					value: Array.from(props.value as LabelValue<number>[]),
					movable: item.value.toString(),
					dropTarget: potentialDropTarget
				})
				setPotentialDropTarget(null)
				onUpdateValueArray(updatedValue as LabelValue<number>[])
			},
			hover(item, monitor) {
				const itemsLength = Array.from(props.value as LabelValue<number>[]).length
				const refsLength = Object.values(itemsRefs).length
				dragDropService.updateItemCoords(itemsRefs)

				if (
					itemsLength !== 0 &&
					refsLength === itemsLength &&
					Object.values(itemsRefs).every(({ current }) => current)
				) {
					const movingCoords = monitor.getSourceClientOffset() as XYCoord
					setPotentialDropTarget(
						dragDropService.getPotentialDropTarget({
							name: item.value.toString(),
							...movingCoords
						})
					)
				}
			}
		})

		const tagRender = (props: CustomTagProps) => (
			<DraggableItem
				{...props}
				ref={itemsRefs[props.value?.toString() as string]}
				potentialDropTarget={potentialDropTarget}
				resetPotentialDropTarget={() => setPotentialDropTarget(null)}
			/>
		)

		return (
			<div ref={drop}>
				<MultiSelect ref={ref} {...props} tagRender={tagRender} />
			</div>
		)
	}
)

export default MultiselectWithDraggableItems
